package link.erpango.quip.ui

import androidx.compose.foundation.background
import androidx.compose.foundation.clickable
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.lazy.LazyColumn
import androidx.compose.foundation.lazy.items
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.horizontalScroll
import androidx.compose.material.CircularProgressIndicator
import androidx.compose.material.ExperimentalMaterialApi
import androidx.compose.material.Icon
import androidx.compose.material.IconButton
import androidx.compose.material.MaterialTheme
import androidx.compose.material.Text
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.filled.Add
import androidx.compose.material.icons.filled.ArrowBack
import androidx.compose.material.icons.filled.Build
import androidx.compose.material.icons.filled.Clear
import androidx.compose.material.icons.filled.Search
import androidx.compose.material.icons.filled.Warning
import androidx.compose.material.pullrefresh.PullRefreshIndicator
import androidx.compose.material.pullrefresh.pullRefresh
import androidx.compose.material.pullrefresh.rememberPullRefreshState
import androidx.compose.runtime.Composable
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.text.font.FontWeight
import link.erpango.quip.domain.entities.DMySparePart
import link.erpango.quip.domain.entities.DVehicle
import link.erpango.quip.features.myspareparts.MySparePartsUiState
import link.erpango.quip.theme.QuipColors
import link.erpango.quip.theme.QuipDimens
import link.erpango.quip.ui.components.QuipCard
import link.erpango.quip.ui.components.QuipDropdownTextField
import link.erpango.quip.ui.components.QuipPrimaryButton
import link.erpango.quip.ui.components.QuipSecondaryButton
import link.erpango.quip.ui.components.QuipTextBadge
import link.erpango.quip.ui.components.QuipTextField
import link.erpango.quip.ui.components.QuipTransparentHeader
import org.jetbrains.compose.resources.stringResource
import quip.composeapp.generated.resources.Res
import quip.composeapp.generated.resources.my_spare_parts_add_first
import quip.composeapp.generated.resources.my_spare_parts_all_vehicles
import quip.composeapp.generated.resources.my_spare_parts_compatible_models
import quip.composeapp.generated.resources.my_spare_parts_empty_message
import quip.composeapp.generated.resources.my_spare_parts_empty_title
import quip.composeapp.generated.resources.my_spare_parts_error_generic
import quip.composeapp.generated.resources.my_spare_parts_search_hint
import quip.composeapp.generated.resources.my_spare_parts_title
import quip.composeapp.generated.resources.reference_label
import quip.composeapp.generated.resources.retry
import quip.composeapp.generated.resources.vehicle

/**
 * Pantalla "Mis repuestos": lista de los repuestos personales del usuario con búsqueda
 * (debounceada) y filtro por vehículo del garaje.
 */
@OptIn(ExperimentalMaterialApi::class)
@Composable
fun MySparePartsListScreen(
    state: MySparePartsUiState,
    vehicles: List<DVehicle>,
    onSearchQueryChange: (String) -> Unit,
    onVehicleFilterChange: (String?) -> Unit,
    onRefresh: () -> Unit,
    onRetry: () -> Unit,
    onAddPartClick: () -> Unit,
    onPartClick: (DMySparePart) -> Unit,
    onBackClick: () -> Unit
) {
    val pullRefreshState = rememberPullRefreshState(state.isRefreshing, onRefresh)

    Column(
        modifier = Modifier
            .fillMaxSize()
            .background(color = QuipColors.colorBackground)
    ) {
        QuipTransparentHeader {
            Row(
                modifier = Modifier.fillMaxWidth(),
                verticalAlignment = Alignment.CenterVertically
            ) {
                IconButton(onClick = onBackClick) {
                    Icon(
                        imageVector = Icons.Default.ArrowBack,
                        contentDescription = stringResource(Res.string.my_spare_parts_title),
                        tint = QuipColors.colorText
                    )
                }
                Spacer(modifier = Modifier.width(QuipDimens.padding8))
                Text(
                    text = stringResource(Res.string.my_spare_parts_title),
                    style = MaterialTheme.typography.h4,
                    fontWeight = FontWeight.Bold,
                    color = QuipColors.colorText,
                    modifier = Modifier.weight(1f)
                )
                IconButton(onClick = onAddPartClick) {
                    Icon(
                        imageVector = Icons.Default.Add,
                        contentDescription = stringResource(Res.string.my_spare_parts_add_first),
                        tint = QuipColors.colorText
                    )
                }
            }
        }

        Column(
            modifier = Modifier.padding(horizontal = QuipDimens.padding16)
        ) {
            QuipTextField(
                value = state.searchQuery,
                onValueChange = onSearchQueryChange,
                label = stringResource(Res.string.my_spare_parts_search_hint),
                icon = Icons.Default.Search,
                trailingIcon = if (state.searchQuery.isNotEmpty()) {
                    {
                        IconButton(onClick = { onSearchQueryChange("") }) {
                            Icon(
                                imageVector = Icons.Default.Clear,
                                contentDescription = stringResource(Res.string.my_spare_parts_search_hint),
                                tint = QuipColors.colorTextSecondary,
                                modifier = Modifier.size(QuipDimens.iconSize20)
                            )
                        }
                    }
                } else null
            )
            Spacer(modifier = Modifier.height(QuipDimens.padding8))

            val allVehiclesLabel = stringResource(Res.string.my_spare_parts_all_vehicles)
            val selectedVehicle: DVehicle? = vehicles.find { it.vin == state.selectedVehicleVin }
            QuipDropdownTextField<DVehicle?>(
                options = listOf<DVehicle?>(null) + vehicles,
                selectedOption = selectedVehicle,
                itemToStringTitle = { vehicle ->
                    if (vehicle != null) "${vehicle.brand} ${vehicle.model}" else allVehiclesLabel
                },
                label = stringResource(Res.string.vehicle),
                onOptionSelected = { _, option ->
                    onVehicleFilterChange(option?.vin)
                },
                onClearOption = { onVehicleFilterChange(null) }
            )
            Spacer(modifier = Modifier.height(QuipDimens.padding12))
        }

        Box(
            modifier = Modifier
                .weight(1f)
                .pullRefresh(pullRefreshState)
        ) {
            when {
                state.isLoading && state.parts.isEmpty() -> MySparePartsLoadingContent()
                state.error != null && state.parts.isEmpty() -> MySparePartsErrorContent(
                    message = state.error.ifBlank { stringResource(Res.string.my_spare_parts_error_generic) },
                    onRetry = onRetry
                )
                state.parts.isEmpty() -> MySparePartsEmptyContent(onAddPartClick = onAddPartClick)
                else -> MySparePartsListContent(
                    parts = state.parts,
                    vehicles = vehicles,
                    onPartClick = onPartClick
                )
            }
            PullRefreshIndicator(
                refreshing = state.isRefreshing,
                state = pullRefreshState,
                modifier = Modifier.align(Alignment.TopCenter)
            )
        }
    }
}

@Composable
private fun MySparePartsLoadingContent() {
    Box(modifier = Modifier.fillMaxSize(), contentAlignment = Alignment.Center) {
        CircularProgressIndicator(color = QuipColors.colorPrimary)
    }
}

@Composable
private fun MySparePartsErrorContent(
    message: String,
    onRetry: () -> Unit
) {
    Column(
        modifier = Modifier
            .fillMaxSize()
            .padding(QuipDimens.padding24),
        horizontalAlignment = Alignment.CenterHorizontally,
        verticalArrangement = Arrangement.Center
    ) {
        Icon(
            imageVector = Icons.Default.Warning,
            contentDescription = null,
            tint = QuipColors.colorError,
            modifier = Modifier.size(QuipDimens.iconSize48)
        )
        Spacer(modifier = Modifier.height(QuipDimens.padding16))
        Text(
            text = message,
            style = MaterialTheme.typography.body1,
            color = QuipColors.colorTextSecondary
        )
        Spacer(modifier = Modifier.height(QuipDimens.padding24))
        QuipPrimaryButton(
            text = stringResource(Res.string.retry),
            onClick = onRetry
        )
    }
}

@Composable
private fun MySparePartsEmptyContent(onAddPartClick: () -> Unit) {
    Column(
        modifier = Modifier
            .fillMaxSize()
            .padding(QuipDimens.padding24),
        horizontalAlignment = Alignment.CenterHorizontally,
        verticalArrangement = Arrangement.Center
    ) {
        Icon(
            imageVector = Icons.Default.Build,
            contentDescription = null,
            tint = QuipColors.colorPrimary.copy(alpha = 0.4f),
            modifier = Modifier.size(QuipDimens.iconSize48)
        )
        Spacer(modifier = Modifier.height(QuipDimens.padding16))
        Text(
            text = stringResource(Res.string.my_spare_parts_empty_title),
            style = MaterialTheme.typography.h6,
            fontWeight = FontWeight.Bold,
            color = QuipColors.colorText
        )
        Spacer(modifier = Modifier.height(QuipDimens.padding8))
        Text(
            text = stringResource(Res.string.my_spare_parts_empty_message),
            style = MaterialTheme.typography.body1,
            color = QuipColors.colorTextSecondary
        )
        Spacer(modifier = Modifier.height(QuipDimens.padding24))
        QuipPrimaryButton(
            text = stringResource(Res.string.my_spare_parts_add_first),
            onClick = onAddPartClick,
            icon = Icons.Default.Add
        )
    }
}

@Composable
private fun MySparePartsListContent(
    parts: List<DMySparePart>,
    vehicles: List<DVehicle>,
    onPartClick: (DMySparePart) -> Unit
) {
    LazyColumn(modifier = Modifier.fillMaxSize()) {
        items(parts, key = { it.id }) { part ->
            MySparePartCard(
                part = part,
                vehicles = vehicles,
                onClick = { onPartClick(part) }
            )
        }
    }
}

/**
 * Card de un repuesto personal. Se reutiliza en la lista global y en la sección
 * "Repuestos" del detalle de vehículo (ahí el padding horizontal lo aporta el contenedor).
 */
@Composable
fun MySparePartCard(
    part: DMySparePart,
    vehicles: List<DVehicle>,
    onClick: () -> Unit,
    modifier: Modifier = Modifier.padding(horizontal = QuipDimens.padding16, vertical = QuipDimens.padding6)
) {
    val categoryLabel = getSparePartCategoryString("spare_part.category.${part.categoryId}")
    val typeLabel = getSparePartTypeString("spare_part.type.${part.sparePartId}")

    QuipCard(
        modifier = modifier.clickable(onClick = onClick)
    ) {
        Column(modifier = Modifier.fillMaxWidth()) {
            Text(
                text = categoryLabel,
                style = MaterialTheme.typography.caption,
                fontWeight = FontWeight.Bold,
                color = QuipColors.colorPrimary
            )
            Spacer(modifier = Modifier.height(QuipDimens.padding4))
            Text(
                text = typeLabel,
                style = MaterialTheme.typography.h6,
                color = QuipColors.colorText
            )
            Text(
                text = "${part.brand} ${part.model}",
                style = MaterialTheme.typography.body1,
                fontWeight = FontWeight.SemiBold,
                color = QuipColors.colorText
            )
            if (!part.reference.isNullOrBlank()) {
                Spacer(modifier = Modifier.height(QuipDimens.padding4))
                Text(
                    text = "${stringResource(Res.string.reference_label)}: ${part.reference}",
                    style = MaterialTheme.typography.body2,
                    color = QuipColors.colorTextSecondary
                )
            }

            val linkedVehicles = part.vehicleVins.mapNotNull { vin -> vehicles.find { it.vin == vin } }
            if (linkedVehicles.isNotEmpty() || part.compatibleVehicles.isNotEmpty()) {
                Spacer(modifier = Modifier.height(QuipDimens.padding8))
                Row(
                    modifier = Modifier
                        .fillMaxWidth()
                        .horizontalScroll(rememberScrollState()),
                    horizontalArrangement = Arrangement.spacedBy(QuipDimens.padding6)
                ) {
                    part.vehicleVins.forEach { vin ->
                        val vehicle = vehicles.find { it.vin == vin }
                        QuipTextBadge(
                            text = if (vehicle != null) "${vehicle.brand} ${vehicle.model}" else vin
                        )
                    }
                    if (part.compatibleVehicles.isNotEmpty()) {
                        QuipTextBadge(
                            text = stringResource(
                                Res.string.my_spare_parts_compatible_models,
                                part.compatibleVehicles.size
                            ),
                            backgroundColor = QuipColors.colorSecondary.copy(alpha = 0.4f),
                            textColor = QuipColors.colorText
                        )
                    }
                }
            }
        }
    }
}
